import * as am5 from '@amcharts/amcharts5'
import * as am5map from '@amcharts/amcharts5/map'
import am5geodata_usaLow from '@amcharts/amcharts5-geodata/usaLow'
import am5geodata_worldLow from '@amcharts/amcharts5-geodata/worldLow'
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated'
import {isEmpty} from 'lodash'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {CSSTransition} from 'react-transition-group'
import {toAbsoluteUrl} from '../../../../_gori/helpers'
import useCancelToken from '../../../../_gori/hooks/UseCancelToken'
import {ValidationErrorModal} from '../../../../_gori/partials/widgets'
import {useAuth} from '../../auth'
import {VALUE_SHIPMENT_MAP} from '../../dashboard/core/_const'
import OrganizationService from '../core/_requests'
import {useOrganizationBranches} from '../providers/OrganizationBranchesProvider'
import {
  buildOrganizationAnalyticsPayload,
  OrganizationAnalyticsFilters,
} from '../helpers/organizationAnalyticsHelpers'

type Props = OrganizationAnalyticsFilters

const ShipmentMap: React.FC<Props> = ({accountIds, timeRange, isCustomRange, dateRange}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const intl = useIntl()
  const {cancelAndCreateNewToken, isCancel} = useCancelToken()
  const {loadingSwitch} = useAuth()
  const {isBranchesReady} = useOrganizationBranches()
  const [validationErrors, setValidationErrors] = useState<any>()
  const [shipmentMap, setShipmentMap] = useState<any>()
  const [shipmentMapLoading, setShipmentMapLoading] = useState<boolean>(true)
  const [globalMap, setGlobalMap] = useState<string>(VALUE_SHIPMENT_MAP.TYPE.DOMESTIC)

  const analyticsFilters = useMemo(
    () => ({accountIds, timeRange, isCustomRange, dateRange}),
    [accountIds, dateRange, isCustomRange, timeRange]
  )

  const handleChangGlobalMap = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGlobalMap(e.target.value)
  }

  useEffect(() => {
    if (loadingSwitch || !isBranchesReady) {
      return
    }

    if (!accountIds.length) {
      setShipmentMap({empty: true})
      setShipmentMapLoading(false)
      return
    }

    setShipmentMapLoading(true)
    setShipmentMap({})

    const loadShipmentsMapData = async () => {
      try {
        const response = await OrganizationService.getShipmentsMapData(
          buildOrganizationAnalyticsPayload(analyticsFilters),
          {cancelToken: cancelAndCreateNewToken()}
        )
        setShipmentMap(response)
      } catch (error: any) {
        if (isCancel(error)) return
        setValidationErrors(error?.response)
      } finally {
        setShipmentMapLoading(false)
      }
    }

    loadShipmentsMapData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accountIds,
    cancelAndCreateNewToken,
    dateRange.end,
    dateRange.start,
    isCancel,
    isCustomRange,
    isBranchesReady,
    loadingSwitch,
    timeRange,
  ])

  const showMapLoading = !isBranchesReady || shipmentMapLoading

  useEffect(() => {
    if (shipmentMapLoading || !chartRef.current || isEmpty(shipmentMap)) {
      return
    }

    let root = am5.Root.new('chart-dashboard-shipments-map')
    root.setThemes([am5themes_Animated.new(root)])
    root?._logo?.dispose()

    if (globalMap === VALUE_SHIPMENT_MAP.TYPE.GLOBAL) {
      // Create the map chart
      // https://www.amcharts.com/docs/v5/charts/map-chart/
      let chart = root.container.children.push(
        am5map.MapChart.new(root, {
          panX: 'translateX',
          panY: 'translateY',
          projection: am5map.geoMercator(),
        })
      )
      chart.root.dom.style.height = '225px'
      // chart.root.dom.style.width = '900px'

      // World Series
      // https://www.amcharts.com/docs/v5/charts/map-chart/map-polygon-series/
      let polygonSeriesWorld = chart.series.push(
        am5map.MapPolygonSeries.new(root, {
          geoJSON: am5geodata_worldLow,
          valueField: 'value',
          calculateAggregates: true,
          exclude: ['AQ'],
          fill: am5.color('#F1F4F9'),
        })
      )

      polygonSeriesWorld.mapPolygons.template.setAll({
        tooltipText: '{name}: {value}',
      })
      polygonSeriesWorld.set('heatRules', [
        {
          target: polygonSeriesWorld.mapPolygons.template,
          dataField: 'value',
          min: am5.color('#B5E0D7'),
          max: am5.color('#114643'),
          key: 'fill',
        },
      ])
      // Screenshot UI focuses on destination volume -> always use "to" datasets.
      polygonSeriesWorld.data.setAll(Object.values(shipmentMap?.global?.to ?? []))

      // @ts-ignore
      chart.chartContainer.get('background').events.on('click', function () {
        chart.goHome()
      })
      chart.appear(1000, 100)
    } else {
      let chart = root.container.children.push(
        am5map.MapChart.new(root, {
          projection: am5map.geoAlbersUsa(),
          layout: root.horizontalLayout,
        })
      )
      chart.root.dom.style.height = '225px'
      // chart.root.dom.style.width = '900px'

      let polygonSeries = chart.series.push(
        am5map.MapPolygonSeries.new(root, {
          geoJSON: am5geodata_usaLow,
          valueField: 'value',
          calculateAggregates: true,
          exclude: ['antarctica'],
          fill: am5.color('#F1F4F9'),
        })
      )

      polygonSeries.mapPolygons.template.setAll({
        tooltipText: '{name}: {value}',
      })
      polygonSeries.set('heatRules', [
        {
          target: polygonSeries.mapPolygons.template,
          dataField: 'value',
          min: am5.color('#B5E0D7'),
          max: am5.color('#114643'),
          key: 'fill',
          // customFunction: function(sprite, min, max, value) {
          //   if (value < 100) {
          //     sprite.set('', am5.color(0xff0000));
          //   }
          //   else {
          //     sprite.set('backgroundColor', am5.color(0x00ff00));
          //   }
          // }
        },
      ])
      polygonSeries.data.setAll(Object.values(shipmentMap?.domestic?.to ?? []))

      // @ts-ignore
      chart.chartContainer.get('background').events.on('click', function () {
        chart.goHome()
      })
      chart.appear(1000, 100)
    }

    return () => {
      root.dispose()
    }
  }, [globalMap, shipmentMap, shipmentMapLoading])

  const formatCompactNumber = (value: number) => {
    if (!Number.isFinite(value)) return '0'
    if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
    if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(1)}k`
    return `${Math.round(value)}`
  }

  const getTopRegions = () => {
    const raw =
      globalMap === VALUE_SHIPMENT_MAP.TYPE.GLOBAL
        ? shipmentMap?.global?.to_top_region
        : shipmentMap?.domestic?.to_top_region

    if (!raw) return []
    if (Array.isArray(raw)) return raw
    return Object.values(raw)
  }

  const shipmentRegion = () => {
    const topRegions = getTopRegions()
    const legendColors = ['#F1F4F9', '#B5E0D7', '#79D1C3', '#34BFB0', '#0D9488', '#114643']

    return (
      <>
        <div className='organizations__shipment-map-legend-section'>
          {showMapLoading ? (
            <>
              <div className='organizations__shipment-map-skeleton-title placeholder-wave' />
              <div className='organizations__shipment-map-skeleton-legend placeholder-wave' />
              <div className='organizations__shipment-map-skeleton-title placeholder-wave' />
              <div className='organizations__shipment-map-skeleton-performers placeholder-wave' />
            </>
          ) : (
            <>
              <div className='organizations__shipment-map-legend-header'>
                <div className='organizations__shipment-map-legend-title'>
                  {intl.formatMessage({id: 'SHIPMENT_DESTINATION_VOLUME'})}
                </div>
              </div>

              <div className='organizations__shipment-map-legend-body'>
                <div className='organizations__shipment-map-legend'>
                  <div className='organizations__shipment-map-legend-bar' aria-hidden='true'>
                    {legendColors.map((color, idx) => (
                      <div
                        key={color + idx}
                        style={{backgroundColor: color}}
                        className='organizations__shipment-map-legend-seg'
                      />
                    ))}
                  </div>

                  <div className='organizations__shipment-map-legend-scale'>
                    <span>0</span>
                    <span>X</span>
                  </div>
                </div>

                <div className='organizations__shipment-map-performers'>
                  <div className='organizations__shipment-map-performers-title'>
                    {intl.formatMessage({id: 'TOP_5_PERFORMERS'})}
                  </div>
                  <div className='organizations__shipment-map-performers-grid'>
                    {topRegions.slice(0, 5).map((region: any) => (
                      <div
                        key={region?.name ?? Math.random()}
                        className='organizations__shipment-map-performer-card'
                      >
                        <div className='organizations__shipment-map-performer-name'>
                          {region?.name}
                        </div>
                        <div className='organizations__shipment-map-performer-value'>
                          {formatCompactNumber(Number(region?.value))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </>
    )
  }

  const noShipmentFound = () => {
    return (
      <div className='mt-5 mb-5 no-shipment-dashboard text-center'>
        <img
          src={toAbsoluteUrl('/media/gori/dashboard/no_shipment_found.svg')}
          className='w-200px'
          alt=''
        />
        <div
          className='no-shipment-dashboard-text'
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage({id: 'NO_SHIPMENTS_FOUND'}),
          }}
        ></div>
      </div>
    )
  }
  return (
    <>
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => {
            setValidationErrors(undefined)
          }}
          response={validationErrors}
        />
      )}
      <CSSTransition in timeout={300} classNames='fade' unmountOnExit>
        <div className='organizations__shipment-map card card-xl-stretch shadow-sm w-100 h-100'>
          <div className='card-body d-flex flex-column justify-content-between'>
            <div className='d-flex flex-wrap gap-3 pb-3 justify-content-between'>
              {showMapLoading ? (
                <>
                  <h1 className='col-6 placeholder-wave'>
                    <span className='col-5 placeholder placeholder-lg rounded-2 bg-secondary' />
                  </h1>
                  <div className='col-6 d-flex flex-row-reverse placeholder-wave'>
                    <span className='btn col-3 placeholder placeholder-lg rounded-2 bg-secondary me-2' />
                  </div>
                </>
              ) : (
                <>
                  <div className='d-flex align-items-center text-dark fw-bolder opacity-75 fs-3'>
                    {intl.formatMessage({id: 'SHIPMENTS_MAP'})}
                  </div>
                  <div className='organizations__shipment-map-toggle d-flex flex-wrap gap-2 align-items-center'>
                    <div className='d-flex align-items-center form-check m-0'>
                      <input
                        className='form-check-input organizations__shipment-map-toggle-input'
                        type='radio'
                        name='checkedInternationalShipment'
                        id='checkedInternationalShipmentDomestic'
                        value={VALUE_SHIPMENT_MAP.TYPE.DOMESTIC}
                        checked={globalMap === VALUE_SHIPMENT_MAP.TYPE.DOMESTIC}
                        onChange={handleChangGlobalMap}
                      />
                      <label
                        className='ps-2 form-check-label text-uppercase organizations__shipment-map-toggle-label'
                        htmlFor='checkedInternationalShipmentDomestic'
                      >
                        {intl.formatMessage({id: 'DOMESTIC'})}
                      </label>
                    </div>
                    <div className='d-flex align-items-center form-check m-0'>
                      <input
                        className='form-check-input organizations__shipment-map-toggle-input'
                        type='radio'
                        name='checkedInternationalShipment'
                        id='checkedInternationalShipmentIntl'
                        value={VALUE_SHIPMENT_MAP.TYPE.GLOBAL}
                        checked={globalMap === VALUE_SHIPMENT_MAP.TYPE.GLOBAL}
                        onChange={handleChangGlobalMap}
                      />
                      <label
                        className='ps-2 form-check-label text-uppercase organizations__shipment-map-toggle-label'
                        htmlFor='checkedInternationalShipmentIntl'
                      >
                        {intl.formatMessage({id: 'INTERNATIONAL'})}
                      </label>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div
              className='min-h-auto ps-4 pe-6 w-100'
              ref={chartRef}
              id='chart-dashboard-shipments-map'
            />
            <div className='col-12'>
              {shipmentMap?.empty ? noShipmentFound() : shipmentRegion()}
            </div>
          </div>
        </div>
      </CSSTransition>
    </>
  )
}

export {ShipmentMap}
